import { parseISO } from "date-fns";

import { Button, Link, TiVoTargetsSummary } from "components/elements";
import {
  Section,
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableNoResults,
  TableRow,
  TableRowColumn,
} from "components/layout";
import { ALL_MODERATORS, ROLES, RestrictedArea } from "containers/Permissions";
import { useManifests } from "hooks/manifests";
import { formatDateTime } from "utils/date";

import { PATHS } from "../../constants";
import { getCreatePath, getDetailsPath } from "../../utils";
import { TEXTS } from "./constants";
import styles from "./ManifestsTab.scss";

export interface ManifestsTabProps {
  appId: string;
}

export function ManifestsTab({ appId }: ManifestsTabProps) {
  const {
    data: manifestsData,
    error,
    isLoading,
  } = useManifests({
    provider_id: [appId],
  });
  const manifests = manifestsData?.results || [];

  const renderTableBody = () => {
    if (manifests.length === 0) {
      return (
        <TableNoResults
          isError={error}
          loading={isLoading}
          message={error ? error.message : undefined}
        />
      );
    }
    return (
      <TableBody>
        {manifests.map(
          ({ id, provider_id, created_date, modified_date, targets }) => {
            return (
              <TableRow key={id}>
                <TableRowColumn>
                  <Link to={getDetailsPath(id)}>{provider_id}</Link>
                </TableRowColumn>
                <TableRowColumn>
                  <TiVoTargetsSummary targets={targets} />
                </TableRowColumn>
                <TableRowColumn>
                  {formatDateTime(parseISO(String(created_date)))}
                </TableRowColumn>
                <TableRowColumn>
                  {formatDateTime(parseISO(String(modified_date)))}
                </TableRowColumn>
              </TableRow>
            );
          }
        )}
      </TableBody>
    );
  };

  return (
    <RestrictedArea allowed={[...ALL_MODERATORS, ROLES.tivo.preview]}>
      <RestrictedArea
        allowed={[ROLES.moderator.appIntegrator]}
        showFallback={false}
      >
        <Button
          className={styles.editButton}
          to={getCreatePath(PATHS.createDefaultTab, appId)}
          type="green"
          dataTestId="create-manifest-button"
        >
          {TEXTS.MANIFESTS.CREATE_MANIFEST_BUTTON_LABEL}
        </Button>
      </RestrictedArea>
      <Section header={TEXTS.MANIFESTS.HEADER}>
        <Table>
          <TableHeader loading={isLoading}>
            <TableHeaderColumn className={styles.idCol}>
              {TEXTS.MANIFESTS.APP_ID_COL_HEADER}
            </TableHeaderColumn>
            <TableHeaderColumn className={styles.targetCol}>
              {TEXTS.MANIFESTS.TARGET_COL_HEADER}
            </TableHeaderColumn>
            <TableHeaderColumn className={styles.dateCol}>
              {TEXTS.MANIFESTS.CREATION_DATE_COL_HEADER}
            </TableHeaderColumn>
            <TableHeaderColumn className={styles.dateCol}>
              {TEXTS.MANIFESTS.MOD_DATE_COL_HEADER}
            </TableHeaderColumn>
          </TableHeader>
          {renderTableBody()}
        </Table>
      </Section>
    </RestrictedArea>
  );
}
